import { useState } from 'react';
import { MoreHorizontal, Plus, Search, Tag as TagIcon, X } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import {
  MemberInviteDialog,
  useMembers,
  useResendInvite,
  useSetMemberActive,
  useSetMemberTags,
  useSetMemberWhiteLabel,
  type Member,
  type MemberType,
} from '@/features/members';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

const TYPE_FILTERS: { value: 'all' | MemberType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'staff', label: 'Staff' },
  { value: 'guest', label: 'Guest' },
];

/** Mirrors the Status column: Pending (never signed in) wins over Active/Inactive. */
type StatusFilter = 'all' | 'active' | 'pending' | 'inactive';
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
];

/** Inline chip editor for a member's free-text tags (org-admins only). */
function TagsEditor({ tags, onSave }: { tags: string[]; onSave: (tags: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(tags);
  const [input, setInput] = useState('');

  function addTag() {
    const value = input.trim().slice(0, 50);
    if (value && !draft.includes(value)) setDraft([...draft, value]);
    setInput('');
  }

  function removeTag(value: string) {
    setDraft(draft.filter((t) => t !== value));
  }

  function commit(next: boolean) {
    setOpen(next);
    if (next) {
      // Seed the editor from the current tags each time it opens.
      setDraft(tags);
      setInput('');
      return;
    }
    const cleaned = input.trim() ? [...draft, input.trim().slice(0, 50)] : draft;
    const deduped = [...new Set(cleaned)];
    if (JSON.stringify(deduped) !== JSON.stringify(tags)) onSave(deduped);
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.length ? (
        tags.map((t) => (
          <Badge key={t} variant="secondary">
            {t}
          </Badge>
        ))
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      )}
      <Popover open={open} onOpenChange={commit}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="size-6" aria-label="Edit tags">
            <TagIcon className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-3" align="end">
          <div className="flex flex-wrap gap-1">
            {draft.length ? (
              draft.map((t) => (
                <Badge key={t} variant="secondary" className="gap-1">
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="hover:text-destructive"
                    aria-label={`Remove ${t}`}
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground text-xs">No tags yet.</span>
            )}
          </div>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Type a tag, press Enter"
            maxLength={50}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function MemberRow({
  member,
  canManage,
  onResend,
  resendingId,
  onToggleWhiteLabel,
  onToggleActive,
  onSaveTags,
}: {
  member: Member;
  canManage: boolean;
  onResend: (id: number) => void;
  resendingId: number | null;
  onToggleWhiteLabel: (id: number, value: boolean) => void;
  onToggleActive: (id: number, value: boolean) => void;
  onSaveTags: (id: number, tags: string[]) => void;
}) {
  const isGuest = member.type === 'guest';
  return (
    <TableRow>
      <TableCell className="font-medium">{member.user.full_name}</TableCell>
      <TableCell className="text-muted-foreground">{member.user.email}</TableCell>
      <TableCell>
        <Badge variant={isGuest ? 'outline' : 'secondary'}>{isGuest ? 'Guest' : 'Staff'}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {member.roles.length ? (
            member.roles.map((role) => (
              <Badge key={role} variant="outline">
                {role}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {canManage ? (
          <TagsEditor tags={member.tags ?? []} onSave={(tags) => onSaveTags(member.id, tags)} />
        ) : (member.tags?.length ?? 0) ? (
          <div className="flex flex-wrap gap-1">
            {member.tags!.map((t) => (
              <Badge key={t} variant="secondary">
                {t}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        {member.is_pending ? (
          <Badge variant="outline" className="text-amber-600 dark:text-amber-500">
            Pending
          </Badge>
        ) : member.is_active ? (
          <span className="text-sm">Active</span>
        ) : (
          <span className="text-muted-foreground text-sm">Inactive</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {canManage ? (
          <div className="flex items-center justify-end gap-3">
            {/* Subcontracted (white-label) — guests only: hides the org switcher
                + agency branding for that guest. Kept inline as a quick toggle. */}
            {isGuest ? (
              <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <Switch
                  checked={member.white_label ?? false}
                  onCheckedChange={(v) => onToggleWhiteLabel(member.id, v)}
                  aria-label="White-label (subcontracted client)"
                  title="White-label — hide the agency's branding for this subcontracted client"
                />
                White-label
              </span>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Member actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {member.is_pending ? (
                  <DropdownMenuItem
                    disabled={resendingId === member.id}
                    onClick={() => onResend(member.id)}
                  >
                    {resendingId === member.id ? 'Sending…' : 'Resend invite'}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={() => onToggleActive(member.id, !member.is_active)}>
                  {member.is_active ? 'Deactivate' : 'Reactivate'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function MembersListPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | MemberType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const role = useSession((s) => s.role);
  // UI gate (F2) — only org admins manage membership; the server is authority.
  const canManage = role === 'founder' || role === 'super_admin';

  const { data, isPending, isError, error, isFetching } = useMembers({
    search: search || undefined,
    type: typeFilter === 'all' ? undefined : typeFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
    // Roster manages everyone, including terminated employees (pickers hide them).
    include_inactive: true,
  });
  const resend = useResendInvite();
  const setWhiteLabel = useSetMemberWhiteLabel();
  const setActive = useSetMemberActive();
  const setTags = useSetMemberTags();
  // Deactivate/reactivate cascades (employee status, login) — confirm first.
  const [pendingActive, setPendingActive] = useState<{ member: Member; value: boolean } | null>(
    null,
  );

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  const members = data?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Members &amp; Roles</h1>
          <p className="text-muted-foreground text-sm">
            People with access to this organization.
          </p>
        </div>
        {canManage ? (
          <Button onClick={() => setInviteOpen(true)}>
            <Plus /> Add member
          </Button>
        ) : null}
      </div>

      {canManage ? (
        <MemberInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="relative w-full max-w-sm">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search by name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <div
          role="group"
          aria-label="Filter by type"
          className="border-input bg-background inline-flex overflow-hidden rounded-md border"
        >
          {TYPE_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTypeFilter(value)}
              aria-pressed={typeFilter === value}
              className={cn(
                'inline-flex h-9 items-center px-3 text-sm transition-colors',
                typeFilter === value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label="Filter by status"
          className="border-input bg-background inline-flex overflow-hidden rounded-md border"
        >
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatusFilter(value)}
              aria-pressed={statusFilter === value}
              className={cn(
                'inline-flex h-9 items-center px-3 text-sm transition-colors',
                statusFilter === value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load members.'}
          </p>
        )
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="sr-only">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : members.length ? (
                members.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    canManage={canManage}
                    onResend={(id) => resend.mutate(id)}
                    resendingId={resend.isPending ? (resend.variables ?? null) : null}
                    onToggleWhiteLabel={(id, value) =>
                      setWhiteLabel.mutate({ id, whiteLabel: value })
                    }
                    onToggleActive={(_, value) => setPendingActive({ member, value })}
                    onSaveTags={(id, tags) => setTags.mutate({ id, tags })}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground text-center">
                    No members found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog
        open={pendingActive !== null}
        onOpenChange={(o) => !o && setPendingActive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingActive?.value ? 'Reactivate' : 'Deactivate'}{' '}
              {pendingActive?.member.user.full_name || 'this member'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingActive?.value
                ? 'Restores their access to this workspace and re-enables their login. A terminated employee record is set back to Active.'
                : 'They lose access to this workspace, their employee record is set to Terminated, and their login is disabled unless they belong to another organization. Reversible — reactivating restores everything.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingActive) {
                  setActive.mutate({ id: pendingActive.member.id, isActive: pendingActive.value });
                }
                setPendingActive(null);
              }}
            >
              {pendingActive?.value ? 'Reactivate' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
